import React, { useRef, useState, useContext } from "react";
import type Account from "byzantine/src/Account";
import useAccountSorting from "../hooks/useAccountSorting";
import useAccountBalances from "../hooks/useAccountBalances";

type AccountContextValue = {
  accounts: Account[];
  internalAccounts: Account[];
  externalAccounts: Account[];
  favoritedAccounts: Account[];
  unfavoritedAccounts: Account[];
  hiddenAccounts: Account[];
  depositAccounts: Account[];
  setAccounts: React.Dispatch<React.SetStateAction<Account[]>>;
  updateAccount: (account: Account) => void;
  getAccountSortingForGroup: (groupName: string) => Record<string, unknown>;
  clearSorting: (groupName: string) => Promise<void>;
  sortGroupAlphabetically: (groupName: string, order?: string) => Promise<void>;
  sortGroupByBalance: (groupName: string, order?: string) => Promise<void>;
  sortGroupByAccountNumber: (
    groupName: string,
    order?: string
  ) => Promise<void>;
  sortAscending: (groupName: string, sortBy: string) => Promise<void>;
  sortDescending: (groupName: string, sortBy: string) => Promise<void>;
  isUpdatingAccounts: boolean;
  accountUpdateInFlightRef: React.MutableRefObject<boolean>;
};

type AccountContextProviderProps = {
  children?: React.ReactNode;
  accounts: Account[];
};

const AccountContext = React.createContext({} as AccountContextValue);

export const useAccountsContext = () => useContext(AccountContext);

export const AccountContextProvider = ({
  children,
  accounts: accountsProp,
}: AccountContextProviderProps) => {
  const [accounts, setAccounts] = useState(accountsProp);
  const [isUpdatingAccounts, setIsUpdatingAccounts] = useState(false);
  const accountUpdateInFlightRef = useRef(false);
  const { orderedAccounts, sortGroup, getAccountSortingForGroup } =
    useAccountSorting(accounts);

  const updateAccount = (account: Account) => {
    setAccounts((prevAccounts) => {
      const index = prevAccounts.findIndex((a) => a.id === account.id);
      if (index === -1) {
        return [...prevAccounts, account];
      }
      const nextAccounts = [...prevAccounts];
      nextAccounts[index] = account;
      return nextAccounts;
    });
  };

  useAccountBalances({ setAccounts, setIsUpdatingAccounts });

  const internalAccounts = orderedAccounts.filter((a) => a.isInternal());
  const externalAccounts = orderedAccounts.filter((a) => !a.isInternal());
  const favoritedAccounts = internalAccounts.filter((a) => a.favorited);
  const unfavoritedAccounts = internalAccounts.filter(
    (a) => !a.favorited && !a.hidden
  );
  const hiddenAccounts = internalAccounts.filter((a) => a.hidden);
  const depositAccounts = orderedAccounts.filter(
    (account) => account.account_type === "deposit"
  );

  return (
    <AccountContext.Provider
      value={{
        accounts: orderedAccounts,
        internalAccounts,
        externalAccounts,
        favoritedAccounts,
        unfavoritedAccounts,
        hiddenAccounts,
        depositAccounts,
        setAccounts,
        updateAccount,
        getAccountSortingForGroup,
        clearSorting: (groupName) => sortGroup(groupName),
        sortGroupAlphabetically: (groupName, order) =>
          sortGroup(groupName, "alphabetically", order),
        sortGroupByBalance: (groupName, order) =>
          sortGroup(groupName, "by_balance", order),
        sortGroupByAccountNumber: (groupName, order) =>
          sortGroup(groupName, "by_account_number", order),
        sortAscending: (groupName, sortBy) =>
          sortGroup(groupName, sortBy, "ascending"),
        sortDescending: (groupName, sortBy) =>
          sortGroup(groupName, sortBy, "descending"),
        isUpdatingAccounts,
        accountUpdateInFlightRef,
      }}
    >
      {children}
    </AccountContext.Provider>
  );
};

export default AccountContext;
